import { Box, useTheme } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import * as React from 'react';
import { hexToRgbaString } from '~/utils';

const Content = (props) => <Box {...props} />;
const Trigger = (props) => <Box {...props} />;

const Container = (props) => (
  <Box role="group" position="relative" overflow="hidden" {...props} />
);

// eslint-disable-next-line react/prop-types
const Overlay = ({ position, isAlwaysVisible, ...rest }) => {
  const theme = useTheme();
  let props;
  switch (position) {
    case 'bottom':
      props = {
        transform: isAlwaysVisible ? 'none' : 'translateY(100%)',
        bottom: 0,
      };
      break;
    case 'top':
      props = {
        transform: isAlwaysVisible ? 'none' : 'translateY(-100%)',
        top: 0,
      };
      break;
    case 'cover':
      props = {
        top: 0,
        bottom: 0,
        opacity: isAlwaysVisible ? 1 : 0,
        _groupHover: {
          opacity: 1,
          transition: 'opacity .2s cubic-bezier(0, 0, 0.2, 1)',
        },
      };
      break;
    default:
      break;
  }
  return (
    <Box
      transition="transform .2s ease-in-out, opacity .2s ease-in-out"
      _groupHover={{
        transform: 'translateY(0)',
        transition: 'transform .2s cubic-bezier(0, 0, 0.2, 1)',
      }}
      paddingX="3"
      paddingY="5"
      pos="absolute"
      width="100%"
      zIndex="2"
      backgroundColor={hexToRgbaString(theme.colors.black, 0.3)}
      {...props}
      {...rest}
    />
  );
};

const propTypes = {
  /** Children can be of type `ContentOverlay.Trigger` and `ContentOverlay.Content` */
  children: (props) => {
    let error;
    React.Children.forEach(props.children, (child) => {
      if (child.type !== Trigger && child.type !== Content) {
        error = new Error(
          `\`ContentOverlay\` only accepts children of type \`Trigger\` or \`Content\`. Received ${child.type}`,
        );
      }
    });
    return error;
  },
  position: PropTypes.oneOf(['bottom', 'top', 'cover']),
  /** Forces the overlay to be always visible */
  isAlwaysVisible: PropTypes.bool,
  /** Props passed to the overlay element */
  overlayProps: PropTypes.object,
};

const ContentOverlay = ({
  isAlwaysVisible = false,
  position = 'bottom',
  children,
  overlayProps,
  ...rest
}) => {
  const content = React.Children.toArray(children).find(
    (child) => child.type === Content,
  );

  const trigger = React.Children.toArray(children).find(
    (child) => child.type === Trigger,
  );

  return (
    <Container {...rest}>
      {trigger}
      <Overlay
        position={position}
        isAlwaysVisible={isAlwaysVisible}
        {...overlayProps}
      >
        {content}
      </Overlay>
    </Container>
  );
};

ContentOverlay.Trigger = Trigger;
ContentOverlay.Content = Content;

ContentOverlay.propTypes = propTypes;

export default ContentOverlay;
